import { useState } from 'react'
import {
  IconButton,
  Popover,
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Chip,
  Alert,
  TextField
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RefreshIcon from '@mui/icons-material/Refresh'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'

interface ConnectionStatusMenuProps {
  isAuthenticated: boolean
  authLoading: boolean
  onAuthenticate: () => void
  onLogout: () => void
  hasFubApiKey: boolean
  dotloopEmail?: string | null
  validationError?: string | null
  needsReauth?: boolean
}

export default function ConnectionStatusMenu({
  isAuthenticated,
  authLoading,
  onAuthenticate,
  onLogout,
  hasFubApiKey,
  dotloopEmail,
  validationError,
  needsReauth
}: ConnectionStatusMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [debugLoopId, setDebugLoopId] = useState('')
  const [debugLoading, setDebugLoading] = useState(false)
  const [debugStatus, setDebugStatus] = useState<string | null>(null)
  const [debugError, setDebugError] = useState<string | null>(null)
  const [debugTemplatesLoading, setDebugTemplatesLoading] = useState(false)
  const [debugTemplatesStatus, setDebugTemplatesStatus] = useState<string | null>(null)
  const [debugTemplatesError, setDebugTemplatesError] = useState<string | null>(null)

  const getDebugHeaders = (): HeadersInit => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }

    const iframeAuthToken = localStorage.getItem('dotloop_iframe_auth_token')
    if (iframeAuthToken) {
      headers['X-Iframe-Auth-Token'] = iframeAuthToken
    }

    return headers
  }

  const handleDebugLookup = async () => {
    setDebugStatus(null)
    setDebugError(null)

    const trimmedLoopId = debugLoopId.trim()
    if (!trimmedLoopId) {
      setDebugError('Enter a loop ID to look up.')
      return
    }

    setDebugLoading(true)
    try {
      const response = await fetch(`/api/debug/get-loop?loopId=${encodeURIComponent(trimmedLoopId)}`, {
        credentials: 'include',
        headers: getDebugHeaders()
      })

      const data = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch loop data')
      }

      setDebugStatus('Loop data logged to server console.')
    } catch (error) {
      setDebugError(error instanceof Error ? error.message : 'Failed to fetch loop data')
    } finally {
      setDebugLoading(false)
    }
  }

  const handleDebugTemplates = async () => {
    setDebugTemplatesStatus(null)
    setDebugTemplatesError(null)

    setDebugTemplatesLoading(true)
    try {
      const response = await fetch('/api/debug/get-loop-templates', {
        credentials: 'include',
        headers: getDebugHeaders()
      })

      const data = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch loop templates')
      }

      setDebugTemplatesStatus('Template data logged to server console.')
    } catch (error) {
      setDebugTemplatesError(error instanceof Error ? error.message : 'Failed to fetch loop templates')
    } finally {
      setDebugTemplatesLoading(false)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          position: 'relative',
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        size="small"
      >
        <SettingsIcon />
        {/* Status indicator dot */}
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isAuthenticated ? '#4caf50' : '#ff9800',
            border: '1px solid white'
          }}
        />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 300,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Connection Status
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {/* DotLoop Status */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                {authLoading ? (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <RefreshIcon
                      sx={{
                        fontSize: 18,
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                  </Box>
                ) : isAuthenticated ? (
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                )}
                <Typography variant="body2">DotLoop</Typography>
              </Stack>
              <Chip
                label={authLoading ? 'Checking...' : isAuthenticated ? 'Connected' : needsReauth ? 'Needs Auth' : 'Not Connected'}
                size="small"
                color={isAuthenticated ? 'success' : needsReauth ? 'error' : 'warning'}
                variant={isAuthenticated ? 'filled' : 'outlined'}
              />
            </Stack>

            {/* Show authenticated user if available */}
            {isAuthenticated && dotloopEmail && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 0.5 }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {dotloopEmail}
                </Typography>
              </Stack>
            )}

            {/* Show validation error if present */}
            {validationError && !authLoading && (
              <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                {validationError}
              </Alert>
            )}

            {/* FUB API Status */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                {hasFubApiKey ? (
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                )}
                <Typography variant="body2">FUB API</Typography>
              </Stack>
              <Chip
                label={hasFubApiKey ? 'Configured' : 'Not Configured'}
                size="small"
                color={hasFubApiKey ? 'success' : 'default'}
                variant={hasFubApiKey ? 'filled' : 'outlined'}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Authentication actions */}
          {!isAuthenticated ? (
            <>
              {/* Info alert explaining authentication */}
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
                <Typography variant="caption" display="block">
                  Connect your DotLoop account to create and manage transaction loops.
                </Typography>
              </Alert>
              
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => {
                  onAuthenticate()
                  handleClose()
                }}
              >
                Connect to DotLoop
              </Button>
            </>
          ) : (
            <Stack spacing={1.5}>
              {/* Re-authenticate button */}
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => {
                  onAuthenticate()
                  handleClose()
                }}
              >
                Switch DotLoop Account
              </Button>
              
              {/* Logout button */}
              <Button
                variant="text"
                size="small"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={() => {
                  onLogout()
                  handleClose()
                }}
                sx={{ color: 'text.secondary' }}
              >
                Disconnect DotLoop
              </Button>
            </Stack>
          )}

          {isAuthenticated && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Debug Tools
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Loop ID"
                  size="small"
                  value={debugLoopId}
                  onChange={(event) => setDebugLoopId(event.target.value)}
                  placeholder="Enter loop ID"
                  inputProps={{ inputMode: 'numeric' }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDebugLookup}
                  disabled={debugLoading}
                >
                  {debugLoading ? 'Looking up...' : 'Lookup'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDebugTemplates}
                  disabled={debugTemplatesLoading}
                >
                  {debugTemplatesLoading ? 'Fetching templates...' : 'Get Templates'}
                </Button>
                {debugStatus && (
                  <Alert severity="success" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    {debugStatus}
                  </Alert>
                )}
                {debugError && (
                  <Alert severity="error" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    {debugError}
                  </Alert>
                )}
                {debugTemplatesStatus && (
                  <Alert severity="success" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    {debugTemplatesStatus}
                  </Alert>
                )}
                {debugTemplatesError && (
                  <Alert severity="error" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    {debugTemplatesError}
                  </Alert>
                )}
              </Stack>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}
